// src/views/Public/NfcTeamProfile.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp,
  FaGlobe,
} from 'react-icons/fa';

import { db } from '../../firebase';

import './NfcProfile.css';

const DEFAULT_HIGHLIGHTS = [
  {
    title: 'Respuesta rapida',
    body: 'Atendemos tu solicitud y te guiamos hasta cerrar la venta.',
  },
  {
    title: 'Presentacion profesional',
    body: 'Tu contacto queda guardado con una imagen y mensaje claro.',
  },
  {
    title: 'Soluciones reales',
    body: 'Mostramos lo mas llamativo de nuestros desarrollos.',
  },
];

const DEFAULT_SOLUTIONS = [
  'Implementaciones a medida para tu negocio.',
  'Soporte directo y seguimiento.',
  'Integraciones listas para vender.',
];

const buildVCard = ({ name, phone, email, website, title }) => {
  if (!name && !phone && !email) return '';

  const lines = ['BEGIN:VCARD', 'VERSION:3.0', `FN:${name || 'Contacto'}`];
  if (title) {
    lines.push(`TITLE:${title}`);
  }
  if (phone) {
    lines.push(`TEL;TYPE=CELL:${phone}`);
  }
  if (email) {
    lines.push(`EMAIL:${email}`);
  }
  if (website) {
    lines.push(`URL:${website}`);
  }
  lines.push('END:VCARD');
  return lines.join('\n');
};

const sanitizeFileName = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const normalizeLink = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(candidate);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
  } catch (_) {
    return '';
  }
};

const normalizeSocialLink = (network, value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (network === 'whatsapp') {
    const digits = raw.replace(/\D/g, '');
    return digits.length >= 7 ? `https://wa.me/${digits}` : normalizeLink(raw);
  }
  if (/^https?:\/\//i.test(raw)) return normalizeLink(raw);
  const handle = raw.replace(/^@+/, '').replace(/^\/+/, '');
  const roots = {
    instagram: 'https://www.instagram.com/',
    facebook: 'https://www.facebook.com/',
    linkedin: 'https://www.linkedin.com/in/',
  };
  return roots[network] ? `${roots[network]}${encodeURIComponent(handle)}` : normalizeLink(raw);
};

const NfcTeamProfile = () => {
  const { teamId } = useParams();
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState('loading');
  const [autoDownloadStatus, setAutoDownloadStatus] = useState('idle');
  const [nfcToken, setNfcToken] = useState('');
  const [leadForm, setLeadForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [leadStatus, setLeadStatus] = useState('idle');
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  const autoDownloadRef = useRef(false);
  const nfcApiBase = 'https://us-central1-tarjeta-ad945.cloudfunctions.net';

  useEffect(() => {
    const loadProfile = async () => {
      if (!teamId) {
        setStatus('error');
        return;
      }

      setStatus('loading');
      const candidateId = decodeURIComponent(teamId);

      try {
        const profileSnap = await getDoc(doc(db, 'teamProfiles', candidateId));
        if (!profileSnap.exists()) {
          setStatus('not_found');
          return;
        }

        const data = profileSnap.data();
        setProfile({ id: profileSnap.id, ...data });
        setStatus('ready');
      } catch (err) {
        console.error('❌ Error cargando perfil del equipo:', err);
        setStatus('error');
      }
    };

    loadProfile();
  }, [teamId]);

  useEffect(() => {
    if (!teamId) return;
    fetch(`${nfcApiBase}/createNfcSession`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ teamId }),
    })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => setNfcToken(data?.token || ''))
      .catch(() => setNfcToken(''));
  }, [teamId]);

  const trackNfcAction = (action, detail = '') => {
    fetch(`${nfcApiBase}/recordNfcInteraction`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
      body: JSON.stringify({ teamId, token: nfcToken, action, detail }),
    }).catch(() => {});
  };

  const displayName = profile?.displayName || 'Equipo BBTS';
  const title = profile?.title || profile?.roleLabel || 'Especialista';
  const bio = profile?.bio || 'Soluciones inteligentes para impulsar tu negocio.';
  const imageUrl = profile?.imageUrl || '/logo-bbts.png';
  const highlights =
    Array.isArray(profile?.highlights) && profile.highlights.length
      ? profile.highlights
      : DEFAULT_HIGHLIGHTS;
  const solutions =
    Array.isArray(profile?.solutions) && profile.solutions.length
      ? profile.solutions
      : DEFAULT_SOLUTIONS;
  const promotions =
    Array.isArray(profile?.promotions) && profile.promotions.length
      ? profile.promotions
      : [];
  const publicContact = profile?.publicContact || {};
  const email = publicContact.email || profile?.email || '';
  const socials = profile?.socials || {};
  const whatsappRaw = socials.whatsapp || '';
  const directPhoneRaw = publicContact.phone || publicContact.mainPhone || whatsappRaw;
  const phoneDigits = directPhoneRaw.replace(/[^\d]/g, '');
  const whatsappLink = normalizeSocialLink('whatsapp', whatsappRaw);
  const whatsappHref = whatsappLink
    ? `${whatsappLink}${whatsappLink.includes('?') ? '&' : '?'}text=${encodeURIComponent(`Hola, te contacto desde la tarjeta digital de ${displayName}.${nfcToken ? ` Ref: BBTS-NFC-${nfcToken}` : ''}`)}`
    : '';
  const telLink = phoneDigits ? `tel:${phoneDigits}` : '';
  // No se usa el correo público como destinatario: el alias único pasa por
  // el Inbound Parse ya existente y permite atribuir el email a esta tarjeta.
  const safeTeamId = /^[A-Za-z0-9_-]{6,128}$/.test(decodeURIComponent(teamId || '')) ? decodeURIComponent(teamId) : '';
  const nfcInboxEmail = safeTeamId ? `nfc.${safeTeamId}@bbts-reply.bbtspr.com` : email;
  const nfcEmailHref = nfcInboxEmail
    ? `mailto:${nfcInboxEmail}?subject=${encodeURIComponent(`Contacto desde tarjeta NFC de ${displayName}`)}`
    : '';
  const instagram = normalizeSocialLink('instagram', socials.instagram);
  const facebook = normalizeSocialLink('facebook', socials.facebook);
  const linkedin = normalizeSocialLink('linkedin', socials.linkedin);
  const website = normalizeLink(socials.website || '');

  const vcardPayload = useMemo(
    () =>
      buildVCard({
        name: displayName,
        phone: phoneDigits,
        email,
        website,
        title,
      }),
    [displayName, phoneDigits, email, website, title]
  );

  useEffect(() => {
    setProfileImageFailed(false);
  }, [imageUrl]);

  const triggerDownload = () => {
    if (!vcardPayload) return;
    try {
      const blob = new Blob([vcardPayload], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const fileName = sanitizeFileName(displayName) || 'contacto';
      anchor.href = url;
      anchor.download = `${fileName}.vcf`;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 500);
      setAutoDownloadStatus('done');
      trackNfcAction('contact_saved');
    } catch (err) {
      console.error('❌ Error descargando contacto:', err);
      setAutoDownloadStatus('failed');
    }
  };

  useEffect(() => {
    if (!profile || autoDownloadRef.current || !vcardPayload) return;
    autoDownloadRef.current = true;
    setAutoDownloadStatus('starting');
    const timer = setTimeout(() => triggerDownload(), 700);
    return () => clearTimeout(timer);
  }, [profile, vcardPayload]);

  const submitLead = async (event) => {
    event.preventDefault();
    setLeadStatus('sending');
    try {
      const response = await fetch(`${nfcApiBase}/submitNfcLead`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId, token: nfcToken, ...leadForm }),
      });
      if (!response.ok) throw new Error('No se pudo enviar la solicitud.');
      setLeadStatus('sent');
      setLeadForm({ name: '', phone: '', email: '', message: '' });
    } catch (_) {
      setLeadStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="nfc-profile-page">
        <div className="nfc-profile-shell nfc-status">
          <p>Preparando tu tarjeta digital...</p>
        </div>
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="nfc-profile-page">
        <div className="nfc-profile-shell nfc-status">
          <h2>Perfil no encontrado</h2>
          <p>Completa tu perfil en el panel para activar esta tarjeta.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="nfc-profile-page">
        <div className="nfc-profile-shell nfc-status">
          <h2>Algo salio mal</h2>
          <p>Intenta nuevamente en unos minutos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nfc-profile-page nfc-team-card">
      <div className="nfc-profile-shell">
        <header className="nfc-hero">
          <div className="nfc-hero__topline">
            <span>BBTS · Tarjeta digital</span>
            <button type="button" className="nfc-save-quick" onClick={triggerDownload}>
              + Guardar
            </button>
          </div>
          <div className="nfc-hero__media">
            {!profileImageFailed ? (
              <img
                src={imageUrl}
                alt={displayName}
                className="nfc-hero__avatar"
                onError={() => setProfileImageFailed(true)}
              />
            ) : (
              <div className="nfc-hero__avatar nfc-hero__avatar--fallback" aria-label={displayName}>
                {displayName.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="nfc-hero__content">
            <p className="nfc-kicker">BBTS</p>
            <h1>{displayName}</h1>
            <p className="nfc-tagline">{title}</p>
            <p className="nfc-description">{bio}</p>
            <div className="nfc-actions">
              {telLink && (
                <a className="nfc-btn nfc-btn--primary" href={telLink} onClick={() => trackNfcAction('phone')}>
                  Llamar ahora
                </a>
              )}
              {whatsappHref && (
                <a
                  className="nfc-btn nfc-btn--ghost"
                  href={whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackNfcAction('whatsapp', 'botón principal')}
                >
                  WhatsApp directo
                </a>
              )}
            </div>
            <div className="nfc-contact-row">
              {nfcEmailHref && (
                <a className="nfc-link" href={nfcEmailHref} onClick={() => trackNfcAction('email')}>
                  Enviar email a {displayName}
                </a>
              )}
              {website && (
                <a className="nfc-link" href={website} target="_blank" rel="noreferrer" onClick={() => trackNfcAction('website')}>
                  {website.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>
            <div className="nfc-socials" aria-label="Redes sociales">
              {[
                { label: 'WhatsApp', action: 'whatsapp', href: whatsappHref, icon: <FaWhatsapp /> },
                { label: 'Instagram', action: 'instagram', href: instagram, icon: <FaInstagram /> },
                { label: 'Facebook', action: 'facebook', href: facebook, icon: <FaFacebookF /> },
                { label: 'LinkedIn', action: 'linkedin', href: linkedin, icon: <FaLinkedinIn /> },
                { label: 'Website', action: 'website', href: website, icon: <FaGlobe /> },
              ].filter((item) => item.href).map((item) => (
                <a key={item.label} className="nfc-social" href={item.href} target="_blank" rel="noreferrer" aria-label={item.label} title={item.label} onClick={() => trackNfcAction(item.action)}>
                  {item.icon}
                  <span>{item.label}</span>
                </a>
              ))}
            </div>
          </div>
        </header>

        <section className="nfc-cta-card">
          <div>
            <h2>Contacto instantaneo</h2>
            <p>
              Al tocar la tarjeta, el contacto se descarga automaticamente para
              que sigas la conversacion sin friccion.
            </p>
            {autoDownloadStatus === 'starting' && (
              <p className="nfc-note">Descargando contacto...</p>
            )}
            {autoDownloadStatus === 'done' && (
              <p className="nfc-note">Contacto descargado con exito.</p>
            )}
            {autoDownloadStatus === 'failed' && (
              <p className="nfc-note">
                Si no se descargo, toca el boton para guardar el contacto.
              </p>
            )}
          </div>
          <button
            type="button"
            className="nfc-btn nfc-btn--dark"
            onClick={triggerDownload}
          >
            Guardar contacto
          </button>
        </section>

        <section className="nfc-lead-card">
          <div>
            <p className="nfc-kicker">¿Quieres que te contactemos?</p>
            <h2>Cuéntanos qué necesitas</h2>
            <p>Deja tus datos y el responsable de esta tarjeta recibe tu solicitud directamente en BBTS.</p>
          </div>
          <form className="nfc-lead-form" onSubmit={submitLead} onFocus={() => trackNfcAction('lead_form_opened')}>
            <input required value={leadForm.name} onChange={(event) => setLeadForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="Tu nombre" />
            <input value={leadForm.phone} onChange={(event) => setLeadForm((prev) => ({ ...prev, phone: event.target.value }))} placeholder="Teléfono" />
            <input type="email" value={leadForm.email} onChange={(event) => setLeadForm((prev) => ({ ...prev, email: event.target.value }))} placeholder="Correo electrónico" />
            <textarea value={leadForm.message} onChange={(event) => setLeadForm((prev) => ({ ...prev, message: event.target.value }))} placeholder="¿En qué podemos ayudarte?" rows="3" />
            <button className="nfc-btn nfc-btn--primary" type="submit" disabled={leadStatus === 'sending'}>{leadStatus === 'sending' ? 'Enviando...' : 'Solicitar información'}</button>
            {leadStatus === 'sent' && <p className="nfc-note">Recibimos tu solicitud. Te contactaremos pronto.</p>}
            {leadStatus === 'error' && <p className="nfc-note">No se pudo enviar. Intenta WhatsApp o vuelve a intentar.</p>}
          </form>
        </section>

        <section className="nfc-section">
          <h2>Lo llamativo de nuestros desarrollos</h2>
          <div className="nfc-grid">
            {highlights.map((item, index) => {
              const titleText = typeof item === 'string' ? item : item.title;
              const bodyText = typeof item === 'string' ? '' : item.body;
              return (
                <article key={index} className="nfc-card">
                  <h3>{titleText || `Beneficio ${index + 1}`}</h3>
                  <p>{bodyText || 'Mensaje breve para destacar tu oferta.'}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="nfc-section nfc-section--light">
          <h2>Soluciones que ofrecemos</h2>
          <ul className="nfc-list">
            {solutions.map((solution, index) => (
              <li key={index}>{solution}</li>
            ))}
          </ul>
        </section>

        {promotions.length > 0 && (
          <section className="nfc-section">
            <h2>Promociones y trabajos recientes</h2>
            <div className="nfc-promos">
              {promotions.map((promo, index) => (
                <article key={index} className="nfc-promo-card">
                  {promo.mediaUrl && promo.mediaType?.startsWith('video') ? (
                    <video
                      controls
                      playsInline
                      src={promo.mediaUrl}
                      className="w-full h-[180px] object-cover"
                    />
                  ) : promo.mediaUrl || promo.imageUrl ? (
                    <img
                      src={promo.mediaUrl || promo.imageUrl}
                      alt={promo.title || 'Promocion'}
                      loading="lazy"
                      onError={(event) => { event.currentTarget.closest('.nfc-promo-card')?.classList.add('nfc-promo-card--media-error'); }}
                    />
                  ) : null}
                  <div className="nfc-promo-body">
                    <h3>{promo.title || 'Promocion destacada'}</h3>
                    {promo.link && (
                      <a href={promo.link} target="_blank" rel="noreferrer">
                        Ver mas
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default NfcTeamProfile;
